import { DeleteOutlined, ExportOutlined, ImportOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select, Switch, Typography } from "antd";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { CONDITION_OPERATOR_OPTIONS } from "components/CC/Edges/ConditionEdge/const";
// import { CONDITION_RESULT_OPTIONS } from "components/CC/Edges/ConditionEdge/const"; // TODO: Hidden - BE doesn't use it yet
import {
    conditionFormDefaultValues,
    conditionFormSchema,
    NO_OUTPUT_OPERATORS,
    OUTPUT2_ENABLED_OPERATORS,
    REGEX_OPERATORS,
} from "components/CC/Edges/ConditionEdge/Forms/formSchema";
import { useSelectedEdge } from "components/CC/hooks/useDiagramActions";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { ParameterCategory } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { FilterType } from "types/enums";
import { z } from "zod";

export type ConditionFormData = z.infer<typeof conditionFormSchema>;

interface FormProps {
    onChange?: (data: ConditionFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    defaultValues?: Partial<ConditionFormData>;
    /** Form fields from API */
    formFields?: App.Data.FormFieldData[];
    /** Custom fields from API (Integration or FormStructure) */
    customFields?: CustomFieldData[];
}

export const ConditionForm = ({
    onChange,
    onRegisterValidator,
    formId,
    defaultValues,
    formFields,
    customFields,
}: FormProps) => {
    return (
        <GeneralFormProvider<ConditionFormData>
            id={formId}
            schema={conditionFormSchema}
            defaultValues={{ ...conditionFormDefaultValues, ...defaultValues }}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                formFields={formFields}
                customFields={customFields}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: ConditionFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const FormContent = ({ onChange, onRegisterValidator, formFields, customFields }: FormContentProps) => {
    const { control, trigger } = useGeneralForm<ConditionFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const { selectedEdge } = useSelectedEdge();
    const buildCategories = useBuildParameterPickerCategories({
        t,
        formFields,
        customFields,
        currentNodeId: selectedEdge?.target,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "conditions",
    });

    const categories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.condition.value.ui.allowedCategories],
            }),
        [buildCategories]
    );

    const handleAddField = () => {
        append({ value: "", operator: "", output: "", output2: "" });
    };

    // Check if any row uses a between operator → show output2 column
    const allConditions = useWatch({ control, name: "conditions" });
    const showOutput2Column = useMemo(() => {
        if (!allConditions) return false;
        return allConditions.some((c) => OUTPUT2_ENABLED_OPERATORS.includes(c?.operator as FilterType));
    }, [allConditions]);

    // Column spans: without output2 = 8+8+8, with output2 = 6+6+6+6
    const colSpans = showOutput2Column
        ? { value: 6, operator: 6, output: 6, output2: 6 }
        : { value: 8, operator: 8, output: 8, output2: 0 };

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<ConditionFormData> onChange={onChange} />
            {/* Header with title, Import/Export buttons, and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("edges.condition.forms.settings")}
                </Typography.Title>
                <div className="flex gap-2 items-center">
                    <Button type="default" size="middle" icon={<ImportOutlined />} disabled>
                        {t("edges.condition.forms.import")}
                    </Button>
                    <Button type="default" size="middle" icon={<ExportOutlined />} disabled>
                        {t("edges.condition.forms.export")}
                    </Button>
                    <Controller
                        control={control}
                        name="active"
                        render={({ field }) => (
                            <div className="flex items-center gap-2">
                                <Switch checked={field.value} onChange={field.onChange} />
                                <Typography.Text type="secondary">{t("edges.condition.forms.active")}</Typography.Text>
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("edges.condition.forms.title")}
                >
                    <Input placeholder={t("edges.condition.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("edges.condition.forms.subtitle")}
                >
                    <Input placeholder={t("edges.condition.forms.subtitle_placeholder")} />
                </FormItem>
                {/* TODO: Result select hidden - BE doesn't use it yet
                <FormItem className="w-32 !mb-0" control={control} name="result" label={t("edges.condition.forms.result")}>
                    <Select
                        options={CONDITION_RESULT_OPTIONS.map((opt) => ({
                            value: opt.value,
                            label: t(opt.labelKey),
                        }))}
                    />
                </FormItem>
                */}
            </div>

            {/* Condition fields */}
            {fields.length > 0 && (
                <div className="mb-2">
                    {/* Header row */}
                    <div className="flex gap-2 mb-2">
                        <Row className="flex-1" gutter={16}>
                            <Col span={colSpans.value}>
                                <Typography.Text type="secondary">{t("edges.condition.forms.value")}</Typography.Text>
                            </Col>
                            <Col span={colSpans.operator}>
                                <Typography.Text type="secondary">
                                    {t("edges.condition.forms.operator")}
                                </Typography.Text>
                            </Col>
                            <Col span={colSpans.output}>
                                <Typography.Text type="secondary">{t("edges.condition.forms.output")}</Typography.Text>
                            </Col>
                            {showOutput2Column && (
                                <Col span={colSpans.output2}>
                                    <Typography.Text type="secondary">
                                        {t("edges.condition.forms.output2")}
                                    </Typography.Text>
                                </Col>
                            )}
                        </Row>
                        <div className="w-8" />
                    </div>

                    {/* Field rows */}
                    {fields.map((field, index) => (
                        <ConditionFieldRow
                            key={field.id}
                            index={index}
                            remove={remove}
                            categories={categories}
                            showOutput2Column={showOutput2Column}
                            colSpans={colSpans}
                        />
                    ))}
                </div>
            )}

            {/* Add field button */}
            <Button type="link" icon={<PlusOutlined />} onClick={handleAddField} className="!px-0">
                {t("edges.condition.forms.add_field")}
            </Button>
        </div>
    );
};

interface ColSpans {
    value: number;
    operator: number;
    output: number;
    output2: number;
}

interface ConditionFieldRowProps {
    index: number;
    remove: (index: number) => void;
    categories: ParameterCategory[];
    showOutput2Column: boolean;
    colSpans: ColSpans;
}

const ConditionFieldRow = ({ index, remove, categories, showOutput2Column, colSpans }: ConditionFieldRowProps) => {
    const { control, setValue, trigger } = useGeneralForm<ConditionFormData>();
    const { t } = usePaldockTranslation("components.connection-creator");

    const operator = useWatch({ control, name: `conditions.${index}.operator` });
    const isRegexOperator = REGEX_OPERATORS.includes(operator as FilterType);
    const isOutputDisabled = NO_OUTPUT_OPERATORS.includes(operator as FilterType);
    const isOutput2Enabled = OUTPUT2_ENABLED_OPERATORS.includes(operator as FilterType);

    useEffect(() => {
        if (NO_OUTPUT_OPERATORS.includes(operator as FilterType)) {
            setValue(`conditions.${index}.output`, "");
            setValue(`conditions.${index}.output2`, "");
            return;
        }
        if (!isOutput2Enabled) {
            setValue(`conditions.${index}.output2`, "");
        }
    }, [index, isOutput2Enabled, operator, setValue]);

    // The regex check on "output" only applies while a regex operator is selected — re-validate it
    // when the operator changes, otherwise a stale "invalid regex" error keeps showing after
    // switching away from regex match/not match. Skipped on mount so a freshly added condition
    // doesn't immediately flash a "required" error before the user touches it.
    const isOutputRegexMount = useRef(true);
    useEffect(() => {
        if (isOutputRegexMount.current) {
            isOutputRegexMount.current = false;
            return;
        }
        trigger(`conditions.${index}.output`);
    }, [index, operator, trigger]);

    return (
        <div className="flex gap-2 mb-2 items-start">
            <Row className="flex-1" gutter={16}>
                <Col span={colSpans.value}>
                    <FormItem className="!mb-0" control={control} name={`conditions.${index}.value`}>
                        <TagTextInput
                            placeholder={t("edges.condition.forms.value_placeholder")}
                            categories={categories}
                            mode={STEP_INPUT_POLICIES.condition.value.ui.mode}
                        />
                    </FormItem>
                </Col>
                <Col span={colSpans.operator}>
                    <FormItem className="!mb-0" control={control} name={`conditions.${index}.operator`}>
                        <Select
                            placeholder={t("edges.condition.forms.operator_placeholder")}
                            options={CONDITION_OPERATOR_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: t(opt.labelKey),
                            }))}
                        />
                    </FormItem>
                </Col>
                <Col span={colSpans.output}>
                    <FormItem className="!mb-0" control={control} name={`conditions.${index}.output`}>
                        {isRegexOperator ? (
                            <Input
                                placeholder={t("edges.condition.forms.output_placeholder")}
                                disabled={isOutputDisabled}
                            />
                        ) : (
                            <TagTextInput
                                placeholder={t("edges.condition.forms.output_placeholder")}
                                categories={categories}
                                disabled={isOutputDisabled}
                                mode={STEP_INPUT_POLICIES.condition.output.ui.mode}
                            />
                        )}
                    </FormItem>
                </Col>
                {showOutput2Column && (
                    <Col span={colSpans.output2}>
                        <FormItem className="!mb-0" control={control} name={`conditions.${index}.output2`}>
                            <Input
                                placeholder={t("edges.condition.forms.output2_placeholder")}
                                disabled={!isOutput2Enabled}
                            />
                        </FormItem>
                    </Col>
                )}
            </Row>
            <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => remove(index)}
                className="!text-gray-400 hover:!text-red-500"
            />
        </div>
    );
};
